import * as React from 'react'

import { createPortal } from 'react-dom'

const modalRoot = document.getElementById('modal-root')

interface IProps {
  className?: string
  /** Stable hook for automated testing, emitted as `data-testid` on the portal element. */
  testId?: string
  /**
   * Id of the element naming this dialog, emitted as `aria-labelledby`.
   *
   * Deliberately not `aria-label`: automation tooling resolves an element's
   * identifier as id > aria-label > title > data-testid, so an `aria-label` here
   * would shadow `testId` and make the dialog untargetable by its test id.
   * `aria-labelledby` gives the same accessible name without shadowing.
   */
  labelledBy?: string
  children: React.ReactNode
}

export default class Modal extends React.Component<IProps> {
  private el: Element

  constructor(props: any) {
    super(props)
    this.el = document.createElement('div')

    if (this.props.className) {
      // `classList.add` throws on tokens containing whitespace, so a
      // multi-class string has to be split into individual tokens.
      this.el.classList.add(
        ...this.props.className.split(/\s+/).filter(Boolean),
      )
    }

    this.el.setAttribute('role', 'dialog')
    this.el.setAttribute('aria-modal', 'true')

    if (this.props.testId) {
      this.el.setAttribute('data-testid', this.props.testId)
    }

    if (this.props.labelledBy) {
      this.el.setAttribute('aria-labelledby', this.props.labelledBy)
    }
  }

  public componentDidMount() {
    if (modalRoot) {
      document.body.classList.add('modal_open')
      document.getElementById('main')?.setAttribute('inert', '')
      modalRoot.appendChild(this.el)

      /**
       * Plugins can depends from resize events, f.e. "react-textarea-autosize", but Portal not provide this.
       * We need to provide this event, otherwise they can be work not correctly.
       */
      if (typeof Event === 'function') {
        window.dispatchEvent(new Event('resize'))
      }
    }
  }

  public componentWillUnmount() {
    if (modalRoot) {
      if (modalRoot.childNodes.length <= 1) {
        document.body.classList.remove('modal_open')
        document.getElementById('main')?.removeAttribute('inert')
      }
      modalRoot.removeChild(this.el)
    }
  }

  public render() {
    return createPortal(this.props.children, this.el)
  }
}
